import { useState } from "react";
import { toast } from "sonner";
import { Eye, Check, X, Undo2, Ban, Loader2, CornerUpLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusPill } from "@/components/StatusPill";
import { getErrorMessage } from "@/lib/errors";

const inr = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const ACTION_LABEL = {
  SUBMITTED: "Submitted for approval", APPROVE: "Approved", REJECT: "Rejected", SEND_BACK: "Sent back for changes",
  WITHDRAW: "Withdrawn by requester", REVOKE: "Approval cancelled",
};
const ACTION_TONE = {
  APPROVE: "text-emerald-600 dark:text-emerald-400", REJECT: "text-rose-600 dark:text-rose-400", REVOKE: "text-rose-600 dark:text-rose-400",
  SEND_BACK: "text-amber-600 dark:text-amber-400", WITHDRAW: "text-amber-600 dark:text-amber-400",
};

/**
 * "View" button for an approval document (PI, PR, PO, WO, SR): full details + approval history, and —
 * depending on the user's role and the document's state — Approve, Reject, Send back for changes,
 * Withdraw, or Cancel approval.
 */
export function ApprovalReviewButton({ docType, docId, onChanged, label }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null); // "reject" | "send_back" | "revoke" | "withdraw"
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setData(null);
    try {
      const { data: res } = await api.get(`/doc-approvals/${docType}/${docId}`);
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
      setOpen(false);
    }
  };

  const openIt = () => { setOpen(true); setMode(null); setComment(""); load(); };

  const run = async (action) => {
    setBusy(true);
    try {
      if (action === "approve") await api.post(data.endpoints.approve, { comment: comment || null });
      else if (action === "reject") await api.post(data.endpoints.reject, { comment: comment || null });
      else if (action === "revoke") await api.post(`/doc-approvals/${docType}/${docId}/revoke`, { comment });
      else await api.post(`/doc-approvals/${docType}/${docId}/send-back`, { comment: comment || null });
      toast.success({ approve: "Approved", reject: "Rejected", send_back: "Sent back to the requester for changes", withdraw: "Withdrawn — back to draft", revoke: "Approval cancelled — back to draft" }[action]);
      setOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const a = data?.actions || {};
  const h = data?.header;
  const needsComment = mode === "send_back" || mode === "revoke";

  return (
    <>
      <Button size="sm" variant="outline" onClick={openIt} title="View details / approval" data-testid={`view-${docType}-${docId}`}>
        <Eye className="h-3.5 w-3.5" /> {label || "View"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          {!data ? (
            <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">{h.label} {h.number} <StatusPill value={h.status} /></DialogTitle>
              </DialogHeader>
              <div className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                {[["Date", h.date], ["Project", h.project], ["Vendor", h.vendor && `${h.vendor}${h.vendor_gstin ? ` (${h.vendor_gstin})` : ""}`],
                  ["Site location", h.site_location], ["Scope of work", h.scope], ["Remarks", h.remarks]]
                  .filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex gap-2"><span className="w-28 shrink-0 text-muted-foreground">{k}</span><span className="text-foreground">{v}</span></div>
                  ))}
              </div>

              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs text-muted-foreground">
                    <tr><th className="p-2 text-left font-medium">#</th><th className="p-2 text-left font-medium">Description</th><th className="p-2 text-right font-medium">Qty</th>
                      <th className="p-2 text-right font-medium">Rate</th><th className="p-2 text-right font-medium">Amount</th></tr>
                  </thead>
                  <tbody>
                    {data.lines.map((l, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-2 text-xs text-muted-foreground">{i + 1}</td>
                        <td className="p-2 text-foreground">{l.description}{l.is_asset && <span className="ml-1.5 rounded bg-violet-500/10 px-1 text-[10px] text-violet-600">Asset</span>}{l.discount_pct && <span className="ml-1 text-[11px] text-muted-foreground">(−{Number(l.discount_pct)}%)</span>}</td>
                        <td className="p-2 text-right font-mono text-xs">{Number(l.quantity)} {l.uom}</td>
                        <td className="p-2 text-right font-mono text-xs">{l.rate ? inr(l.rate) : "—"}</td>
                        <td className="p-2 text-right font-mono text-xs">{l.amount ? inr(l.amount) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="ml-auto w-full max-w-xs space-y-0.5 text-sm">
                {h.subtotal && <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="font-mono">{inr(h.subtotal)}</span></div>}
                {h.tax_amount && Number(h.tax_amount) > 0 && <div className="flex justify-between text-muted-foreground"><span>GST</span><span className="font-mono">{inr(h.tax_amount)}</span></div>}
                <div className="flex justify-between font-semibold text-foreground"><span>Total</span><span className="font-mono">{inr(h.total_amount)}</span></div>
              </div>

              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Approval history</p>
                {data.history.length === 0 ? <p className="text-xs text-muted-foreground">Not submitted through an approval workflow yet.</p> : (
                  <ol className="space-y-1.5 border-l border-border pl-3 text-sm">
                    {data.history.map((e, i) => (
                      <li key={i}>
                        <span className={ACTION_TONE[e.action] || "text-foreground"}>{ACTION_LABEL[e.action] || e.action}</span>
                        <span className="text-muted-foreground"> · {e.who}{e.step ? ` (${e.step})` : ""}{e.when ? ` · ${new Date(e.when).toLocaleString("en-IN")}` : ""}</span>
                        {e.comment && <div className="text-xs text-muted-foreground">“{e.comment}”</div>}
                      </li>
                    ))}
                  </ol>
                )}
                {data.waiting_for && h.status === "SUBMITTED" && <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">Waiting for: {data.waiting_for.replaceAll("_", " ")}</p>}
                {h.status === "APPROVED" && data.revoke_blocked_reason && <p className="mt-2 text-xs text-muted-foreground">Approval can't be cancelled because {data.revoke_blocked_reason}.</p>}
              </div>

              {mode && (
                <div className="space-y-1.5 rounded-lg border border-border p-3">
                  <Label>{{ reject: "Reason for rejecting (optional)", send_back: "What should be changed?", revoke: "Why is the approval being cancelled?", withdraw: "Note (optional)" }[mode]}</Label>
                  <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} autoFocus />
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setMode(null); setComment(""); }}>Back</Button>
                    <Button size="sm" variant={mode === "send_back" || mode === "withdraw" ? "outline" : "destructive"} disabled={busy || (needsComment && !comment.trim())} onClick={() => run(mode)}>
                      {busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirm
                    </Button>
                  </div>
                </div>
              )}

              {!mode && (a.approve || a.reject || a.send_back || a.withdraw || a.revoke) && (
                <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-3">
                  {a.send_back && <Button variant="outline" onClick={() => setMode("send_back")} data-testid="approval-send-back"><CornerUpLeft className="h-4 w-4" /> Send back for review</Button>}
                  {a.withdraw && <Button variant="outline" onClick={() => setMode("withdraw")} data-testid="approval-withdraw"><Undo2 className="h-4 w-4" /> Withdraw</Button>}
                  {a.revoke && <Button variant="outline" onClick={() => setMode("revoke")} data-testid="approval-revoke"><Ban className="h-4 w-4 text-rose-500" /> Cancel approval</Button>}
                  {a.reject && <Button variant="outline" onClick={() => setMode("reject")} data-testid="approval-reject"><X className="h-4 w-4 text-rose-500" /> Reject</Button>}
                  {a.approve && <Button disabled={busy} onClick={() => run("approve")} data-testid="approval-approve">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve</Button>}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
